import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  ReceiptText,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";

import { confirmPaymentForExistingOrder } from "../../actions/paymentActions";
import {
  getOrderByNumber,
  resetOrderDetails,
  resetOrderError,
  resetOrderMeta,
} from "../../actions/orderActions";
import Meta from "../../components/Meta";
import { PAYMENT_PROVIDERS } from "../../constants/paymentConstants";
import {
  formatMoney,
  getFulfillmentStatusLabel,
  getOrderCurrency,
  getOrderItems,
  getOrderNumber,
  getPaymentStatusLabel,
  safeText,
} from "../orders/orderViewUtils";
import { getSafeUserMessage } from "../../utils/errorMessages";
import api from "../../utils/axiosInstance";
import CheckoutRecoveryState from "../orders/CheckoutRecoveryState";

import "../orders/OrderScreen.css";

const SUCCESS_PAYMENT_STATUSES = new Set(["paid", "partially_refunded", "refunded"]);
const FAILED_PAYMENT_STATUSES = new Set(["failed", "cancelled", "canceled"]);

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

export function getPaymentStatus(order = {}) {
  return normalizeLower(order?.payment?.status || order?.paymentStatus || "");
}

export function paymentIsSuccessful(order = {}) {
  return SUCCESS_PAYMENT_STATUSES.has(getPaymentStatus(order));
}

export function paymentIsFailed(order = {}) {
  return FAILED_PAYMENT_STATUSES.has(getPaymentStatus(order));
}

export function CheckoutCancelScreen() {
  const { orderNumber = "" } = useParams();
  const [searchParams] = useSearchParams();
  const safeOrderNumber = safeText(orderNumber);
  const stripeCancelled = safeText(searchParams.get("stripe_cancelled"));
  const titleOrderNumber = safeOrderNumber ? ` ${safeOrderNumber}` : "";

  return (
    <main className="order-detail">
      <Meta
        title={`Order payment cancelled${titleOrderNumber} | Academy Costumes London`}
        description="Your Academy Costumes London card payment was cancelled."
        robots="noindex, nofollow"
      />

      <section className="order-detail__empty">
        <XCircle size={32} aria-hidden="true" />
        <h1>Payment cancelled</h1>
        <p>
          {stripeCancelled
            ? "Stripe returned you before card payment was completed."
            : "Card payment was not completed for this order."}
        </p>
        {safeOrderNumber ? <p>Order {safeOrderNumber}</p> : null}
        <div className="order-detail__inline-actions">
          <Link
            className="order-detail__button order-detail__button--dark"
            to="/checkout"
          >
            Return to checkout
          </Link>
        </div>
      </section>
    </main>
  );
}

export default function CheckoutSuccessScreen() {
  const { orderNumber = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const details = useSelector((state) => state.order?.details || {});
  const order = getOrderNumber(details.order || {}) === safeText(orderNumber) ? details.order : null;
  const safeOrderNumber = getOrderNumber(order) || safeText(orderNumber);
  const stripeSessionId = safeText(
    searchParams.get("stripe_session_id") || searchParams.get("session_id"),
  );
  const confirmAttemptedRef = useRef(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(true);

  const loadOrder = useCallback(async () => {
    if (!safeText(orderNumber)) return;

    try {
      setPaymentLoading(true);
      const { data } = await api.get(`/payments/order/${encodeURIComponent(orderNumber)}`);
      if (data.pending && !data.order) {
        if (data.materializedOrderNumber) {
          navigate(`/order/${encodeURIComponent(data.materializedOrderNumber)}/success`, { replace: true });
        } else {
          setCheckout(data);
        }
        return;
      }
      setCheckout(null);
      await dispatch(getOrderByNumber(data.order?.orderNumber || orderNumber));
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not refresh your order right now. Please try again later.",
        ),
      );
    } finally { setPaymentLoading(false); }
  }, [dispatch, orderNumber, navigate]);

  useEffect(() => {
    let active = true;

    async function syncOrderPayment() {
      if (!safeText(orderNumber)) return;

      if (stripeSessionId && !confirmAttemptedRef.current) {
        confirmAttemptedRef.current = true;

        if (active) setConfirmingPayment(true);

        try {
          const confirmed = await dispatch(
            confirmPaymentForExistingOrder({
              orderNumber,
              provider: PAYMENT_PROVIDERS.STRIPE,
              paymentProvider: PAYMENT_PROVIDERS.STRIPE,
            }),
          );

          const confirmedOrderNumber = safeText(
            confirmed?.orderNumber || confirmed?.order?.orderNumber,
          );

          if (
            confirmedOrderNumber &&
            !confirmed?.pending &&
            confirmedOrderNumber !== safeText(orderNumber)
          ) {
            navigate(`/order/${encodeURIComponent(confirmedOrderNumber)}/success`, {
              replace: true,
            });
            return;
          }
        } catch (error) {
          if (active) {
            toast.info(
              getSafeUserMessage(
                error?.message,
                "Payment is still being confirmed. Your order will update shortly.",
              ),
            );
          }
        } finally {
          if (active) setConfirmingPayment(false);
        }
      }

      if (active) await loadOrder();
    }

    syncOrderPayment();

    return () => {
      active = false;
      dispatch(resetOrderError());
      dispatch(resetOrderMeta());
      dispatch(resetOrderDetails());
    };
  }, [dispatch, loadOrder, navigate, orderNumber, stripeSessionId]);

  const currency = getOrderCurrency(order || {});
  const hasOrder = Boolean(order);
  const hasVerifiedPayment = paymentIsSuccessful(order);
  const hasFailedPayment = paymentIsFailed(order);
  const showLoadingState = (details.loading || confirmingPayment || paymentLoading) && !hasOrder && !checkout;
  const pageTitle = hasVerifiedPayment
    ? `Order confirmation ${safeOrderNumber} | Academy Costumes London`
    : `Payment status ${safeOrderNumber} | Academy Costumes London`;
  const pageDescription = hasVerifiedPayment
    ? "Your Academy Costumes London order confirmation."
    : "Your Academy Costumes London payment status.";

  return (
    <main className="order-detail">
      <Meta
        title={pageTitle}
        description={pageDescription}
        robots="noindex, nofollow"
      />

      {showLoadingState ? (
        <section className="order-detail__empty">
          <Loader2 className="order-detail__spin" />
          <p>
            {confirmingPayment
              ? "Checking your payment status..."
              : "Loading your checkout status..."}
          </p>
        </section>
      ) : checkout ? (
        <>
          <CheckoutRecoveryState checkout={checkout} onRefresh={loadOrder} busy={paymentLoading || confirmingPayment} />
          <Link className="order-detail__back" to="/orders">My orders</Link>
        </>
      ) : !hasVerifiedPayment ? (
        <section className="order-detail__empty">
          {hasFailedPayment ? (
            <XCircle size={32} aria-hidden="true" />
          ) : (
            <Loader2 className="order-detail__spin" />
          )}
          <h1>
            {hasFailedPayment
              ? "Payment was not completed"
              : "Payment is being confirmed"}
          </h1>
          <p>
            {hasFailedPayment
              ? "We have not received a successful payment for this order. Your basket is still available so you can try again."
              : "We are checking with the payment provider. We will show your order confirmation after payment is verified."}
          </p>
          {safeOrderNumber ? <p>Order {safeOrderNumber}</p> : null}
          <div className="order-detail__inline-actions">
            <Link
              className="order-detail__button order-detail__button--dark"
              to="/checkout"
            >
              Return to checkout
            </Link>
            {safeOrderNumber ? (
              <Link
                className="order-detail__button"
                to={`/order/${encodeURIComponent(safeOrderNumber)}`}
              >
                View payment status
              </Link>
            ) : null}
          </div>
        </section>
      ) : (
        <>
          <header className="order-detail__hero">
            <div>
              <p className="order-detail__eyebrow">Order confirmation</p>
              <h1>Thank you</h1>
              <p className="order-detail__subtitle">
                {order?.customerCheckout?.message ||
                  "Your payment has been confirmed. We have prepared the details below for your records."}
              </p>
            </div>

            <CheckCircle2 size={44} aria-hidden="true" />
          </header>

          <div className="order-detail__grid">
          <section className="order-detail__panel">
            <header className="order-detail__panel-header">
              <h2>Order {safeOrderNumber}</h2>
              <span>{getOrderItems(order || {}).length} item(s)</span>
            </header>
            <div className="order-detail__panel-body order-detail__facts">
              <div className="order-detail__fact">
                <span>Total</span>
                <strong>{formatMoney(order?.grandTotal, currency)}</strong>
              </div>
              <div className="order-detail__fact">
                <span>Payment</span>
                <strong>{getPaymentStatusLabel(order || {})}</strong>
              </div>
              <div className="order-detail__fact">
                <span>Fulfilment</span>
                <strong>{getFulfillmentStatusLabel(order || {})}</strong>
              </div>
            </div>
          </section>

          <section className="order-detail__panel">
            <header className="order-detail__panel-header">
              <h2>Next steps</h2>
            </header>
            <div className="order-detail__panel-body order-detail__facts">
              <p>
                {hasVerifiedPayment
                  ? "You can review the order details, save a receipt, or return to the shop when you are ready."
                  : "You can review this checkout attempt now. A receipt will be available once payment has been confirmed."}
              </p>
              <div className="order-detail__inline-actions">
                <Link
                  className="order-detail__button order-detail__button--dark"
                  to={`/order/${encodeURIComponent(safeOrderNumber)}`}
                >
                  <ArrowRight size={16} />
                  <span>View order</span>
                </Link>
                {hasVerifiedPayment ? (
                  <Link
                    className="order-detail__button"
                    to={`/order/${encodeURIComponent(safeOrderNumber)}/receipt`}
                  >
                    <ReceiptText size={16} />
                    <span>Receipt</span>
                  </Link>
                ) : null}
              </div>
            </div>
          </section>
          </div>
        </>
      )}
    </main>
  );
}
